// YatraNest Nepal — Internationalization
// Supports: English (en) and Nepali (ne)
// Currency: NPR (Nepali Rupee)

export type Language = 'en' | 'ne';

export const SUPPORTED_LANGUAGES: { code: Language; label: string; nativeLabel: string }[] = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'ne', label: 'Nepali', nativeLabel: 'नेपाली' },
];

export const DEFAULT_LANGUAGE: Language = 'en';

// ─── Currency ─────────────────────────────────────────────────────────────────

export function formatNPR(amount: number, lang: Language = 'en'): string {
  if (lang === 'ne') {
    return `रू ${amount.toLocaleString('ne-NP')}`;
  }
  return `Rs. ${amount.toLocaleString('en-IN')}`;
}

export function formatNumber(n: number, lang: Language = 'en'): string {
  if (lang === 'ne') return n.toLocaleString('ne-NP');
  return n.toLocaleString('en-IN');
}

export function formatDate(dateStr: string, lang: Language = 'en'): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (lang === 'ne') {
    return d.toLocaleDateString('ne-NP', { year: 'numeric', month: 'long', day: 'numeric' });
  }
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ─── Translations ──────────────────────────────────────────────────────────────

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Nav
    'nav.home': 'Home',
    'nav.destinations': 'Destinations',
    'nav.packages': 'Tour Packages',
    'nav.hotels': 'Hotels',
    'nav.vehicles': 'Vehicle Rental',
    'nav.guides': 'Local Guides',
    'nav.discover': 'Discover',
    'nav.agencies': 'Travel Agencies',
    'nav.blog': 'Blog',
    'nav.contact': 'Contact',
    'nav.login': 'Login',
    'nav.register': 'Register',
    'nav.dashboard': 'My Dashboard',
    'nav.bookings': 'My Bookings',
    'nav.profile': 'Profile',
    'nav.signout': 'Sign Out',
    'nav.provider': 'Provider Dashboard',
    'nav.admin': 'Admin Panel',
    'nav.saved': 'Saved Places',
    'nav.chat': 'Messages',
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Something went wrong',
    'common.retry': 'Try Again',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.view': 'View',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.sort': 'Sort',
    'common.all': 'All',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.submit': 'Submit',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.done': 'Done',
    'common.close': 'Close',
    'common.refresh': 'Refresh',
    'common.noData': 'No data available',
    'common.noResults': 'No results found',
    'common.required': 'Required',
    'common.optional': 'Optional',
    // Booking
    'booking.checkout': 'Checkout',
    'booking.details': 'Booking Details',
    'booking.travellers': 'Traveller Info',
    'booking.review': 'Review & Pay',
    'booking.confirm': 'Booking Confirmed',
    'booking.pending': 'Pending',
    'booking.confirmed': 'Confirmed',
    'booking.cancelled': 'Cancelled',
    'booking.completed': 'Completed',
    'booking.payLater': 'Pay Later',
    'booking.payNow': 'Pay Now',
    'booking.total': 'Total',
    'booking.subtotal': 'Subtotal',
    'booking.tax': 'VAT (13%)',
    'booking.serviceFee': 'Service Fee (2%)',
    'booking.discount': 'Discount',
    'booking.coupon': 'Coupon Code',
    'booking.applyCoupon': 'Apply',
    'booking.myBookings': 'My Bookings',
    // Payment
    'payment.esewa': 'eSewa',
    'payment.khalti': 'Khalti',
    'payment.fonepay': 'FonePay',
    'payment.cash': 'Cash / Pay Later',
    'payment.status.unpaid': 'Unpaid',
    'payment.status.pending': 'Pending',
    'payment.status.paid': 'Paid',
    'payment.status.failed': 'Failed',
    'payment.status.refunded': 'Refunded',
    'payment.history': 'Payment History',
    'payment.reference': 'Transaction Reference',
    'payment.refund': 'Refund Status',
    // Dashboard
    'dashboard.overview': 'Overview',
    'dashboard.bookings': 'My Bookings',
    'dashboard.payments': 'Payment History',
    'dashboard.wishlist': 'Wishlist',
    'dashboard.places': 'My Places',
    'dashboard.explorer': 'Explorer Points',
    'dashboard.reviews': 'My Reviews',
    'dashboard.notifications': 'Notifications',
    'dashboard.messages': 'Messages',
    'dashboard.ai': 'AI Assistant',
    'dashboard.settings': 'Settings',
    // Admin
    'admin.dashboard': 'Dashboard',
    'admin.users': 'Users',
    'admin.analytics': 'Analytics',
    'admin.revenue': 'Revenue',
    'admin.bookings': 'Bookings',
    'admin.providers': 'Provider Verification',
    'admin.moderation': 'Moderation',
    'admin.reports': 'Reports',
    'admin.coupons': 'Coupons',
    'admin.featured': 'Featured Listings',
    'admin.notifications': 'Notifications',
    // Community
    'community.discover': 'Discover',
    'community.submit': 'Submit Place',
    'community.leaderboard': 'Leaderboard',
    'community.saved': 'Saved Places',
    'community.profile': 'Explorer Profile',
    // Auth
    'auth.login': 'Login',
    'auth.register': 'Register',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.forgotPassword': 'Forgot Password?',
    'auth.noAccount': "Don't have an account?",
    'auth.hasAccount': 'Already have an account?',
    'auth.signUp': 'Sign Up',
    'auth.signIn': 'Sign In',
  },
  ne: {
    // Nav
    'nav.home': 'गृहपृष्ठ',
    'nav.destinations': 'गन्तव्यहरू',
    'nav.packages': 'यात्रा प्याकेजहरू',
    'nav.hotels': 'होटलहरू',
    'nav.vehicles': 'सवारी भाडा',
    'nav.guides': 'स्थानीय गाइडहरू',
    'nav.discover': 'खोज्नुहोस्',
    'nav.agencies': 'यात्रा एजेन्सीहरू',
    'nav.blog': 'ब्लग',
    'nav.contact': 'सम्पर्क',
    'nav.login': 'लगइन',
    'nav.register': 'दर्ता',
    'nav.dashboard': 'मेरो ड्यासबोर्ड',
    'nav.bookings': 'मेरा बुकिङहरू',
    'nav.profile': 'प्रोफाइल',
    'nav.signout': 'साइन आउट',
    'nav.provider': 'प्रदायक ड्यासबोर्ड',
    'nav.admin': 'एडमिन प्यानल',
    'nav.saved': 'सुरक्षित स्थानहरू',
    'nav.chat': 'सन्देशहरू',
    // Common
    'common.loading': 'लोड हुँदैछ...',
    'common.error': 'केही गलत भयो',
    'common.retry': 'पुनः प्रयास गर्नुहोस्',
    'common.save': 'सुरक्षित गर्नुहोस्',
    'common.cancel': 'रद्द गर्नुहोस्',
    'common.confirm': 'पुष्टि गर्नुहोस्',
    'common.delete': 'मेटाउनुहोस्',
    'common.edit': 'सम्पादन गर्नुहोस्',
    'common.view': 'हेर्नुहोस्',
    'common.search': 'खोज्नुहोस्',
    'common.filter': 'फिल्टर',
    'common.sort': 'क्रमबद्ध',
    'common.all': 'सबै',
    'common.yes': 'हो',
    'common.no': 'होइन',
    'common.submit': 'पेश गर्नुहोस्',
    'common.back': 'पछाडि',
    'common.next': 'अर्को',
    'common.done': 'सम्पन्न',
    'common.close': 'बन्द गर्नुहोस्',
    'common.refresh': 'ताजा गर्नुहोस्',
    'common.noData': 'डेटा उपलब्ध छैन',
    'common.noResults': 'कुनै परिणाम फेला परेन',
    'common.required': 'आवश्यक',
    'common.optional': 'वैकल्पिक',
    // Booking
    'booking.checkout': 'चेकआउट',
    'booking.details': 'बुकिङ विवरण',
    'booking.travellers': 'यात्री जानकारी',
    'booking.review': 'समीक्षा र भुक्तानी',
    'booking.confirm': 'बुकिङ पुष्टि भयो',
    'booking.pending': 'प्रतीक्षारत',
    'booking.confirmed': 'पुष्टि भयो',
    'booking.cancelled': 'रद्द भयो',
    'booking.completed': 'सम्पन्न',
    'booking.payLater': 'पछि भुक्तानी गर्नुहोस्',
    'booking.payNow': 'अहिले भुक्तानी गर्नुहोस्',
    'booking.total': 'कुल',
    'booking.subtotal': 'उपकुल',
    'booking.tax': 'मूल्य अभिवृद्धि कर (१३%)',
    'booking.serviceFee': 'सेवा शुल्क (२%)',
    'booking.discount': 'छुट',
    'booking.coupon': 'कुपन कोड',
    'booking.applyCoupon': 'लागू गर्नुहोस्',
    'booking.myBookings': 'मेरा बुकिङहरू',
    // Payment
    'payment.esewa': 'ईसेवा',
    'payment.khalti': 'खल्ती',
    'payment.fonepay': 'फोनपे',
    'payment.cash': 'नगद / पछि भुक्तानी',
    'payment.status.unpaid': 'भुक्तानी नभएको',
    'payment.status.pending': 'प्रतीक्षारत',
    'payment.status.paid': 'भुक्तानी भयो',
    'payment.status.failed': 'असफल',
    'payment.status.refunded': 'फिर्ता भयो',
    'payment.history': 'भुक्तानी इतिहास',
    'payment.reference': 'कारोबार सन्दर्भ',
    'payment.refund': 'फिर्ता स्थिति',
    // Dashboard
    'dashboard.overview': 'अवलोकन',
    'dashboard.bookings': 'मेरा बुकिङहरू',
    'dashboard.payments': 'भुक्तानी इतिहास',
    'dashboard.wishlist': 'इच्छासूची',
    'dashboard.places': 'मेरा स्थानहरू',
    'dashboard.explorer': 'अन्वेषक अंक',
    'dashboard.reviews': 'मेरा समीक्षाहरू',
    'dashboard.notifications': 'सूचनाहरू',
    'dashboard.messages': 'सन्देशहरू',
    'dashboard.ai': 'AI सहायक',
    'dashboard.settings': 'सेटिङ',
    // Admin
    'admin.dashboard': 'ड्यासबोर्ड',
    'admin.users': 'प्रयोगकर्ताहरू',
    'admin.analytics': 'विश्लेषण',
    'admin.revenue': 'राजस्व',
    'admin.bookings': 'बुकिङहरू',
    'admin.providers': 'प्रदायक प्रमाणीकरण',
    'admin.moderation': 'मध्यस्थता',
    'admin.reports': 'प्रतिवेदनहरू',
    'admin.coupons': 'कुपनहरू',
    'admin.featured': 'विशेष सूचीहरू',
    'admin.notifications': 'सूचनाहरू',
    // Community
    'community.discover': 'खोज्नुहोस्',
    'community.submit': 'स्थान पेश गर्नुहोस्',
    'community.leaderboard': 'लिडरबोर्ड',
    'community.saved': 'सुरक्षित स्थानहरू',
    'community.profile': 'अन्वेषक प्रोफाइल',
    // Auth
    'auth.login': 'लगइन',
    'auth.register': 'दर्ता',
    'auth.email': 'इमेल',
    'auth.password': 'पासवर्ड',
    'auth.forgotPassword': 'पासवर्ड बिर्सनुभयो?',
    'auth.noAccount': 'खाता छैन?',
    'auth.hasAccount': 'पहिले नै खाता छ?',
    'auth.signUp': 'साइन अप',
    'auth.signIn': 'साइन इन',
  },
};

export function getTranslations(lang: Language) {
  return (key: string, fallback?: string): string => {
    return translations[lang]?.[key] ?? translations['en']?.[key] ?? fallback ?? key;
  };
}

export function t(key: string, lang: Language = 'en', fallback?: string): string {
  return translations[lang]?.[key] ?? translations['en']?.[key] ?? fallback ?? key;
}
